/**
 * What: Micro-survey payload and response contracts for the first-party calibration layer.
 * Transport / data-flow: Pure shared types passed between Shopify Web Pixels, Theme App Extensions, and Cloudflare Worker APIs.
 *
 */
import * as z from 'zod';
export const psychographicSegmentValues = [
    'convenience_led_casual',
    'price_sensitive_deal_seeker',
    'brand_loyalist_advocate',
    'utility_focused_pragmatist',
    'design_conscious_trendsetter',
    'values_driven_ethical_buyer',
    'routine_integrated_user',
    'infrequent_luxury_buyer',
    'gift_buyer',
];
export const microSurveyTriggerTypeValues = [
    'high_intent',
    'post_purchase',
    'loyalty',
    'post_fulfillment',
    'welcome_series',
];
export const psychographicSegmentSchema = z.enum(psychographicSegmentValues);
export const microSurveyTriggerTypeSchema = z.enum(microSurveyTriggerTypeValues);
export const microSurveyChoiceSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    icon: z.string().nullable().optional(),
    psychographicSegment: psychographicSegmentSchema.optional(),
    interpretation: z
        .discriminatedUnion('type', [
        z.object({
            type: z.literal('psychographic_segment'),
            value: psychographicSegmentSchema,
        }),
        z.object({
            type: z.literal('purchase_barrier'),
            value: z.string().min(1),
        }),
        z.object({
            type: z.literal('use_case'),
            value: z.string().min(1),
        }),
        z.object({
            type: z.literal('none'),
        }),
    ])
        .optional(),
});
export const microSurveyQuestionSchema = z.object({
    id: z.string().min(1),
    questionKind: z.enum(['choice_single', 'choice_multi', 'visual_choice', 'short_text']).optional(),
    text: z.string().min(1),
    choices: z.array(microSurveyChoiceSchema).min(1).optional(),
});
export const microSurveyTriggerConditionSchema = z.object({
    type: microSurveyTriggerTypeSchema,
    event: z.string().min(1),
    criteria: z.record(z.string(), z.unknown()).optional(),
});
export const microSurveyPayloadSchema = z
    .object({
    schemaVersion: z.literal('micro-survey-payload-v2').optional(),
    surveyId: z.string().min(1),
    questions: z.array(microSurveyQuestionSchema).min(1),
    triggerCondition: microSurveyTriggerConditionSchema.optional(),
    placements: z.array(microSurveyTriggerConditionSchema).min(1).optional(),
})
    .superRefine((payload, ctx) => {
    if (!payload.triggerCondition && !payload.placements) {
        ctx.addIssue({
            code: 'custom',
            path: ['placements'],
            message: 'Micro-survey payload must declare triggerCondition or placements.',
        });
    }
    payload.questions.forEach((question, index) => {
        if (question.questionKind === 'short_text') {
            return;
        }
        if (!question.choices || question.choices.length === 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['questions', index, 'choices'],
                message: 'Choice-based micro-survey questions must declare choices.',
            });
        }
    });
});
export const microSurveyResponseSchema = z.object({
    surveyId: z.string().min(1),
    questionId: z.string().min(1),
    choiceId: z.string().min(1),
    shopDomain: z.string().min(1),
    customerId: z.string().nullable().optional(),
    correlationId: z.string().min(1),
    submittedAt: z.string().min(1),
});
// Static mapping matrix of visual choices to psychographic segments
export const PSYCHOGRAPHIC_MAPPING_MATRIX = {
    // Example 1: Why did you buy?
    reason_fast: 'convenience_led_casual',
    reason_price: 'price_sensitive_deal_seeker',
    reason_brand: 'brand_loyalist_advocate',
    // Example 2: Hesitation / What are you looking for?
    value_durability: 'utility_focused_pragmatist',
    value_aesthetics: 'design_conscious_trendsetter',
    value_sustainability: 'values_driven_ethical_buyer',
    // Example 3: How are you using the product?
    usage_habit: 'routine_integrated_user',
    usage_treat: 'infrequent_luxury_buyer',
    usage_gift: 'gift_buyer',
};
/**
 * Maps a visual choice ID to its corresponding psychographic segment.
 * This is used by the TestFeed engine to translate simple, low-friction storefront choices
 * (like "Needed it fast") into deep, mathematically defensible psychographic profiles
 * (like "convenience_led_casual") for synthetic panel calibration.
 */
export function mapChoiceToPsychographicSegment(choiceId) {
    return PSYCHOGRAPHIC_MAPPING_MATRIX[choiceId] ?? null;
}
/**
 * Returns the registered storefront trigger types for a payload while preserving legacy single-trigger payloads.
 */
export function getMicroSurveyTriggerTypes(payload) {
    const placements = payload.placements ??
        [payload.triggerCondition].filter((condition) => Boolean(condition));
    return placements.map((placement) => placement.type);
}
/**
 * Resolves the semantic meaning of a submitted choice from the authored payload instead of assuming all choices are psychographic.
 */
export function resolveChoiceInterpretation(payload, questionId, choiceId) {
    const question = payload.questions.find((candidate) => candidate.id === questionId);
    const choice = question?.choices?.find((candidate) => candidate.id === choiceId);
    if (!choice) {
        return null;
    }
    if (choice.interpretation) {
        return choice.interpretation;
    }
    if (choice.psychographicSegment) {
        return {
            type: 'psychographic_segment',
            value: choice.psychographicSegment,
        };
    }
    const segment = mapChoiceToPsychographicSegment(choice.id);
    if (segment) {
        return {
            type: 'psychographic_segment',
            value: segment,
        };
    }
    return {
        type: 'none',
    };
}
//# sourceMappingURL=index.js.map